import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  inject,
  input,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DatePipe } from '@angular/common';
import { Router, RouterLink } from '@angular/router';
import { finalize, Observable } from 'rxjs';
import { ClockIconComponent, HoldIconComponent } from '@kuiper/icons';
import {
  IN_PROGRESS_FILTER_OPTIONS,
  IN_PROGRESS_INSIGHTS,
  IN_PROGRESS_ON_HOLD_COLUMNS,
  IN_PROGRESS_TABLE_FILTER_TABS,
  IN_PROGRESS_VIEW_DETAILS_ACTION,
} from './in-progress.constants';
import { COLUMN_IDENTIFIERS } from '../../const';
import { isFeatureFlaggedColumnVisible } from '../../../../common/helpers/feature-flagged-table-columns/feature-flagged-table-columns.helper';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { AvatarComponent } from '../../../../../ui-components/lib/avatar/avatar.component';
import { RelatedRequestsDropdownComponent } from '../../../../../ui-components/lib/related-requests-dropdown/related-requests-dropdown.component';
import { SearchService } from '../../../../../ui-components/lib/search-field/search.service';
import { TruncatePipe } from '../../../../../ui-components/pipes/truncate/truncate.pipe';
import { TrimTooltipDirective } from '../../../../../ui-components/directives/trim-tooltip/trim-tooltip.directive';
import { FilterOption } from '../../../../../ui-components/lib/filter-dropdown/filter-dropdown.model';
import { FilterMapping } from '../../../../../ui-components/lib/filter-dropdown/filter-mapping.enum';
import { SlaDirective } from '../../../../common/directives/sla/sla.directive';
import { ActionListComponent } from '../../../../common/components/action-list/action-list.component';
import { RequestCreatorComponent } from '../../../../common/components/request-creator/request-creator.component';
import { TenantService } from '../../../../common/services/tenant/tenant.service';
import { TenantPathPipe } from '../../../../common/pipes/tenant-path/tenant-path.pipe';
import { FeatureFlagsService } from '../../../../common/services/feature-flags/feature-flags.service';
import { FEATURE_FLAGS } from '../../../../common/constants/feature-flags.constants';
import { FilterTableColByFeatFlagPipe } from '../../../../common/pipes/filter-table-col-by-featFlag/filter-table-col-by-feat-flag.pipe';
import { RequestTypeEnum } from '../../../../common/enums/request-type';
import {
  TableColumns,
  TableFilterTab,
} from '../../../../../ui-components/lib/table/table.model';
import { StepStatusEnum } from '../../../../common/enums/step-status';
import { Insight } from '../../../../common/models/insight.model';
import { ActionList } from '../../../../common/models/action-list.model';
import { DEFAULT_PAGE_SIZE } from '../../../../common/constants/pagination.constants';
import { EMPLOYEE_ROUTES } from '../../../../common/constants/employee.routing.constants';
import { MyOrganizationService } from '../../../../common/services/my-organization/my-organization.service';
import {
  InProgressRequest,
  InProgressRow,
} from '../../../../common/models/inprogress-requests.model';
import { UserType } from '../../../../common/constants/user-types.constants';
import {
  ApiResponse,
  Meta,
} from '../../../../common/models/api-response.model';
import { FilteredTableComponent } from '../../../../common/components/filtered-table/filtered-table.component';
import { FilteredTablePayload } from '../../../../common/components/filtered-table/filtered-table.model';

@Component({
  selector: 'lib-in-progress',
  imports: [
    DatePipe,
    AvatarComponent,
    ActionListComponent,
    TruncatePipe,
    TrimTooltipDirective,
    TranslocoPipe,
    SlaDirective,
    ClockIconComponent,
    RequestCreatorComponent,
    RouterLink,
    TenantPathPipe,
    HoldIconComponent,
    FilteredTableComponent,
    FilterTableColByFeatFlagPipe,
    RelatedRequestsDropdownComponent,
  ],
  templateUrl: './in-progress.component.html',
  styleUrl: './in-progress.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class InProgressComponent implements OnInit {
  readonly EMPLOYEE_ROUTES = EMPLOYEE_ROUTES;
  protected readonly COLUMN_IDENTIFIERS = COLUMN_IDENTIFIERS;
  stepStatusEnum = StepStatusEnum;

  private readonly myOrganizationService = inject(MyOrganizationService);
  private readonly router = inject(Router);
  private readonly searchService = inject(SearchService);
  private readonly transloco = inject(TranslocoService);
  private readonly tenantService = inject(TenantService);
  private readonly featureFlagService = inject(FeatureFlagsService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly showRelatedRequestsColumn = signal(
    this.featureFlagService.getFeatureFlagValue(
      FEATURE_FLAGS.WORKFLOWS.parent_and_child_approach
    )
  );

  /*********** input *********/
  totalItems = input<number>(0);
  meta = signal<Meta | undefined>(undefined);

  /*********** table  *********/
  isLoading = signal<boolean>(false);
  dataSource = signal<InProgressRow[]>([]);
  insights = signal<Insight[]>(IN_PROGRESS_INSIGHTS);

  private readonly viewDetailsAction: ActionList = {
    ...IN_PROGRESS_VIEW_DETAILS_ACTION,
    label: this.transloco.translate(IN_PROGRESS_VIEW_DETAILS_ACTION.label),
  };
  filterOptions: FilterOption[] = IN_PROGRESS_FILTER_OPTIONS.map(option => ({
    ...option,
    label: this.transloco.translate(option.label),
    placeholder: option.placeholder
      ? this.transloco.translate(option.placeholder)
      : '',
  }));

  columns = computed<TableColumns[]>(() =>
    IN_PROGRESS_ON_HOLD_COLUMNS.filter(col =>
      isFeatureFlaggedColumnVisible(col.header, {
        showRelatedRequestsColumn: this.showRelatedRequestsColumn(),
      })
    ).map(column => ({
      ...column,
      header: this.transloco.translate(column.header),
      viewIconRouterLink: (row: any) => {
        return column.viewIconRouterLink
          ? [this.tenantService.tenantPath(column.viewIconRouterLink(row)[0])]
          : [];
      },
    }))
  );

  tableFilterTabs = signal<TableFilterTab[]>(
    IN_PROGRESS_TABLE_FILTER_TABS.map(tab => ({
      ...tab,
      label: this.transloco.translate(tab.label),
    }))
  );
  activeTableFilterTab = computed(
    () => this.payload().tab ?? this.tableFilterTabs()[0]
  );
  filterMapping: Record<string, string> = {
    workflowTypes: `step][${FilterMapping.WORKFLOW_TYPES}`,
    creationDateStart: `step][${FilterMapping.CREATED_AFTER}`,
    creationDateEnd: `step][${FilterMapping.CREATED_BEFORE}`,
    projectIds: FilterMapping.STEP_HAS_FLOW_PROJECT_IDS,
  };
  payload = signal<FilteredTablePayload>({
    page: 1,
    size: DEFAULT_PAGE_SIZE,
    sortColumn: {
      key: '',
      value: 'asc',
    },
    search: '',
    filters: {},
    tab: this.tableFilterTabs()[0],
  });

  ngOnInit(): void {
    this.getInsights();
  }

  getInsights() {
    const keys = ['total_current_tasks', 'total_current_overdue_tasks'];
    this.myOrganizationService
      .getInProgressInsights()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(res => {
        this.insights.set(
          keys.map((key, index) => ({
            ...this.insights()[index],
            value:
              res.data
                .find((insight: Insight) => insight.key === key)
                ?.value?.toString() ?? '0',
          }))
        );
      });
  }

  checkReturnedObservable = (): Observable<ApiResponse<InProgressRequest>> => {
    this.isLoading.set(true);
    this.meta.set(undefined);

    const { page, size, sortColumn, search, filters } = this.payload();

    return this.myOrganizationService
      .getInProgressRequests(
        page,
        size,
        sortColumn,
        this.searchService.checkSearchLength(search),
        this.activeTableFilterTab().key,
        filters
      )
      .pipe(finalize(() => this.isLoading.set(false)));
  };

  handleInProgressRequestsSuccess(res: ApiResponse<InProgressRequest>) {
    this.dataSource.set(
      res.data.map(row => ({
        ...row,
        actionList: [
          {
            ...this.viewDetailsAction,
            routerLinks: this.getTaskRouterLink(row),
          },
        ],
      }))
    );
    this.meta.set(res.meta);
  }

  handleApiCall() {
    this.checkReturnedObservable()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: res => this.handleInProgressRequestsSuccess(res),
      });
  }

  protected getRelatedRequestViewLink = (childFlow: { id: number }) =>
    this.tenantService.tenantPath(
      `${EMPLOYEE_ROUTES.MY_ORGANIZATION.REQUEST}/${RequestTypeEnum.ACTION}/${childFlow.id}`
    );

  getTaskRouterLink(row: InProgressRequest): string[] {
    return [
      this.tenantService.tenantPath(
        `${EMPLOYEE_ROUTES.MY_ORGANIZATION.REQUEST_ACTION}/${row.step.flow.id.toString()}/${row.step.id.toString()}/${row.id.toString()}/${UserType.SUPER_MANAGER}`
      ),
    ];
  }

  actionListItemClick(action: string, row: InProgressRequest) {
    if (action === IN_PROGRESS_VIEW_DETAILS_ACTION.key) {
      this.router.navigate(this.getTaskRouterLink(row));
    }
  }
}
